import React, {useEffect, useId, useLayoutEffect, useRef} from 'react';
import './styles.css';

type TextAreaUITypes = 'standart' | 'box';

interface Props {
  /** Lets a separate <label htmlFor> name the textarea. */
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  heading?: string;
  className?: string;
  fieldError?: boolean;
  isMandatory?: boolean;
  uiType?: TextAreaUITypes;
  maxCharacters?: number;
  disabled?: boolean;
  maxHeight?: number;
  /** Says the field must be filled in, without the browser's own validation message. */
  ariaRequired?: boolean;
  /** Says the value is wrong, e.g. alongside fieldError. */
  ariaInvalid?: boolean;
  /** The id of an element that describes the field, such as its error message. */
  ariaDescribedBy?: string;
}

/**
 * Grows a field to fit its text, up to maxHeight; it never shrinks. A panel that shows hidden
 * fields can fit them at once, before anything scrolls to them, rather than a frame later.
 */
export function fitTextArea(box: HTMLTextAreaElement, maxHeight?: number) {
  // A field in a hidden panel (display: none) has nothing to measure. Sizing it to 0px would
  // keep it collapsed once the panel shows, so it keeps its own height until then.
  if (!box.offsetWidth) return;
  // The height includes the border (border-box), which scrollHeight leaves out.
  const needed = box.scrollHeight + box.offsetHeight - box.clientHeight;
  const current = parseFloat(box.style.height) || 0;
  box.style.height = `${
    maxHeight && needed > maxHeight ? maxHeight : Math.max(current, needed)
  }px`;
}

const TextAreaInput = React.memo(
  ({
    id: idProp,
    value,
    onChange,
    placeholder,
    heading,
    className,
    fieldError,
    isMandatory,
    uiType,
    maxCharacters,
    disabled,
    maxHeight,
    ariaRequired,
    ariaInvalid,
    ariaDescribedBy,
  }: Props) => {
    const textbox = useRef<any>(null);
    const generatedId = useId();
    const id = idProp ?? generatedId;

    const adjustHeight = () => {
      if (textbox.current) fitTextArea(textbox.current, maxHeight);
    };

    useLayoutEffect(adjustHeight, [value]);

    // The text wraps differently when the field's width changes, e.g. when a hidden panel shows
    // it or the page narrows, so it is measured again then. The next frame measures it: resizing
    // the field from its own observer's callback would loop.
    useEffect(() => {
      const box = textbox.current;
      if (!box || typeof ResizeObserver === 'undefined') return;
      let width = box.offsetWidth;
      let frame = 0;
      const observer = new ResizeObserver(() => {
        if (box.offsetWidth === width) return;
        width = box.offsetWidth;
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(adjustHeight);
      });
      observer.observe(box);
      return () => {
        cancelAnimationFrame(frame);
        observer.disconnect();
      };
      // adjustHeight reads the field when it runs; only its maxHeight comes from this render.
    }, [maxHeight]);

    const handleKeyDown = (e: any) => {
      onChange(e.target.value);
      adjustHeight();
    };

    const getBaseClass = () => {
      switch (uiType) {
        case 'box':
          return 'TextAreaBoxInput';
        default:
          return 'TextAreaInput';
      }
    };

    return (
      <div className={className}>
        <p className="TextAreaHeading">
          {heading}
          {isMandatory ? <span className="MandatoryIndicator">*</span> : ''}
        </p>
        <div className="TextAreaContainer">
          <textarea
            id={id}
            disabled={disabled}
            value={value}
            ref={textbox}
            onChange={handleKeyDown}
            placeholder={placeholder}
            className={`${getBaseClass()} ${fieldError ? 'FieldError' : ''} ${
              disabled ? 'DisabledInput' : ''
            }`}
            maxLength={maxCharacters}
            aria-required={ariaRequired || undefined}
            aria-invalid={ariaInvalid || undefined}
            aria-describedby={ariaDescribedBy}
          />
        </div>
        <div className="TextAreaCharactersCountContainer">
          {maxCharacters ? (
            <div className="TextAreaMaxCharacters">
              <span>
                {value.length} / {maxCharacters}
              </span>
            </div>
          ) : (
            <div />
          )}
        </div>
      </div>
    );
  },
);

export default TextAreaInput;
